import { useCallback, useContext, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, Grid, IconButton, Snackbar, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
// Our Componentes
import { AuthContext } from 'context/auth-context';
import {
    CARD_FORM_ERROR,
    CLOSE_EDIT_CARD_FUNDS_MODAL,
    OPEN_CONFIRM_EXIT_DIALOG,
    SET_CARD,
    UPDATE_CARD_LIST,
    UPDATE_CARDS_METADATA
} from 'context/cards/types';
import { CardContext } from 'context/cards/cardContext';
import EditCardFundsForm from './EditCardFundsForm';
import EditCardInfoForm from './EditCardInfoForm';
// Services
import { Cards, Overview } from 'lib/api';
// Utils and Helpers
import { CARD_TYPE } from 'helpers/constants';
import { MUI_CLOSE_MODAL_REASONS } from 'helpers/constants';
import dayjs from 'helpers/dayjs';
// Assets
import physicalCardBackside from 'assets/cardsSection/cardBackgroud.svg';
import virtualCardBackside from 'assets/createCards/virtualCardBackside.png';
import './EditCardFunds.css';

const CARD_VALUES_TO_UPDATE = {
    autoLock: '',
    label: '',
    memo: ''
};
const EditCardFunds = () => {
    const { card, dispatch, errorMessage, isEditCardFundsModalOpen, isFormSubmitting, previousCard } = useContext(CardContext);
    const { businessInfo } = useContext(AuthContext);
    const [didFormValuesChange, setDidFormValuesChange] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

    const fetchOverviewInfo = useCallback(async () => {
        try {
            const statistics = await Overview.getOverviewInfo();
            dispatch({
                type: UPDATE_CARDS_METADATA,
                payload: statistics
            });
        } catch (error) {
            console.error(error);
        }
    }, [dispatch]);

    const fetchCard = useCallback(async () => {
        try {
            const cardData = await Cards.getCard(card.id);
            dispatch({
                type: SET_CARD,
                payload: cardData
            });
        } catch (error) {
            console.error(error);
        }
    }, [card.id, dispatch]);

    const handleCardFundsFormModalClose = (event, reason) => {
        if (isFormSubmitting) return;
        if (card?.id && didFormValuesChange && MUI_CLOSE_MODAL_REASONS.includes(reason)) {
            return dispatch({ type: OPEN_CONFIRM_EXIT_DIALOG });
        }
        dispatch({ type: CLOSE_EDIT_CARD_FUNDS_MODAL });
    };

    const handleEditCardFunds = async (values, actions) => {
        actions.setSubmitting(true);
        try {
            const body = {
                fundingAmount: values.fundingAmount
            };
            const updatedCard = await Cards.addFunds(card?.id, body);
            dispatch({
                type: UPDATE_CARD_LIST,
                payload: updatedCard
            });
            fetchOverviewInfo();
            fetchCard();
            actions.resetForm();
            setIsSnackbarOpen(true);
        } catch (error) {
            dispatch({
                type: CARD_FORM_ERROR,
                payload: error?.response?.data?.message
            });
        }
        actions.setSubmitting(false);
    };

    const handleEditCardInfo = async (values, actions) => {
        setIsSubmitting(true);
        try {
            const body = mapValuesToUpdateCardModel(values);
            if (Object.keys(body).length === 0) return setIsSubmitting(false);
            const updatedCard = await Cards.updateCard(body, card?.id);
            dispatch({
                type: UPDATE_CARD_LIST,
                payload: updatedCard
            });
            dispatch({ type: CLOSE_EDIT_CARD_FUNDS_MODAL });
            setIsSnackbarOpen(true);
        } catch (error) {
            dispatch({
                type: CARD_FORM_ERROR,
                payload: error?.response?.data?.message
            });
        }
        setIsSubmitting(false);
    };
    const mapValuesToUpdateCardModel = currentCardValues => {
        const body = {};
        for (const key of Object.keys(currentCardValues)) {
            if (
                key in CARD_VALUES_TO_UPDATE &&
                currentCardValues[key] &&
                key === 'autoLock' &&
                dayjs(previousCard[key]).isSame(currentCardValues[key])
            ) {
                continue;
            }
            if (key === 'autoLock' && previousCard[key] !== currentCardValues[key]) {
                body[key] = currentCardValues[key]
                    ? dayjs(currentCardValues[key]).tz('America/Los_Angeles').startOf('day').toISOString()
                    : null;
            }
            if (key === 'memo' && !currentCardValues[key]) {
                body[key] = '';
            }
            if (key in CARD_VALUES_TO_UPDATE && currentCardValues[key] && previousCard[key] !== currentCardValues[key]) {
                body[key] = currentCardValues[key];
            }
        }
        return body;
    };

    return (
        <>
            <Dialog open={isEditCardFundsModalOpen} onClose={() => {}} fullWidth maxWidth="md">
                <Box className="edit-card-funds w-100">
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="30px">
                        <Box className="edit-card-funds__modal-title">Edit Card</Box>

                        <Box alignSelf="start">
                            <IconButton onClick={handleCardFundsFormModalClose}>
                                <Close sx={{ fontSize: '20px', cursor: 'pointer' }} />
                            </IconButton>
                        </Box>
                    </Box>
                    <Grid container columnSpacing={{ md: 3 }}>
                        <Grid item md={6}>
                            <EditCardFundsForm onSubmit={handleEditCardFunds} />
                            <EditCardInfoForm onSubmit={handleEditCardInfo} setDidFormValuesChange={setDidFormValuesChange} />
                        </Grid>
                        <Grid item md={6}>
                            <Box
                                className="edit-card-funds__card-banner-container"
                                sx={{
                                    background: 'linear-gradient(to bottom, rgba(27, 171, 226, 0.2) 50%, #f9f9f8 50%)'
                                }}>
                                <Box className="edit-card-funds__card-image-container" position="relative" sx={{ margin: 0 }}>
                                    <img
                                        className="edit-card-funds__card-image"
                                        src={card.type === CARD_TYPE.VIRTUAL ? virtualCardBackside : physicalCardBackside}
                                        alt="virtual card"
                                    />
                                    <Typography
                                        component="div"
                                        className="edit-card-funds__card-secret edit-card-funds__card-number"
                                        sx={{ fontSize: '12px' }}>
                                        **** **** **** {card?.solidData?.last4}
                                    </Typography>
                                    <Typography
                                        component="div"
                                        className="edit-card-funds__card-secret edit-card-funds__card-expiration"
                                        sx={{ fontSize: '12px' }}>
                                        **/**
                                    </Typography>
                                    <Typography
                                        component="div"
                                        className="edit-card-funds__card-secret edit-card-funds__card-cvv"
                                        sx={{ fontSize: '12px' }}>
                                        ***
                                    </Typography>

                                    <Typography
                                        component="div"
                                        className="edit-card-funds__card-backside-cardholder-name embossing-ellipsis"
                                        sx={{
                                            color: `${
                                                card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'
                                            }`
                                        }}>
                                        {card?.name}
                                    </Typography>
                                    <Typography
                                        component="div"
                                        className="edit-card-funds__card-backside-cardholder-company embossing-ellipsis"
                                        sx={{
                                            color: `${
                                                card?.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'
                                            }`
                                        }}>
                                        {businessInfo.legal_name}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    {errorMessage && (
                        <Alert severity="error" marginBottom="20px">
                            {errorMessage}
                        </Alert>
                    )}
                    <Box display="flex" justifyContent="end" marginTop="20px">
                        <Button
                            variant="contained"
                            type="submit"
                            form="edit-card-info-form"
                            disabled={isSubmitting || !didFormValuesChange}>
                            {isSubmitting ? <CircularProgress size="20px" /> : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                open={isSnackbarOpen}
                onClose={() => setIsSnackbarOpen(false)}
                autoHideDuration={3000}>
                <Alert
                    onClose={() => setIsSnackbarOpen(false)}
                    severity="success"
                    sx={{
                        backgroundColor: '#FFF',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        lineHeight: '22px',
                        color: '#182D55'
                    }}>
                    {`Card ${previousCard?.solidData?.last4} updated successfully`}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EditCardFunds;
